import React from 'react';
import { useEffect, useState } from 'react';
import { Persistor } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';

type Props = {
	children: React.ReactNode;
	loading?: boolean;
	persistor: Persistor;
};

/**
 * SSR-aware PersistGate
 *
 * This component ensures consistent rendering between server-side and client-side environments
 * when using Redux Persist. It handles the differences between server-side rendering (SSR) and
 * client-side rendering (CSR) by rendering children directly on the server and using the
 * `PersistGate` component on the client to manage Redux state persistence.
 *
 * - **Server Behavior**: Renders the `children` directly without wrapping them in `PersistGate`.
 * - **Client Behavior**: Wraps the `children` in `PersistGate` to manage the persistence state.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to render.
 * @param {boolean} [props.loading] - Optional loading indicator to display while the persistor is rehydrating.
 * @param {Persistor} props.persistor - The Redux Persistor instance used to manage state persistence.
 *
 * @returns {JSX.Element} The rendered component, either with `PersistGate` (on the client) or just the `children` (on the server).
 */
export const PersistGateSSR: React.FC<Props> = ({ children, loading, persistor }) => {
	const [isClient, setIsClient] = useState(false);

	// Detect when the component is running on the client
	useEffect(() => {
		setIsClient(true);
	}, []);

	if (!isClient) {
		// Render a placeholder on the server to match the client structure
		return <div>{loading || null}</div>;
	}

	return (
		<PersistGate loading={loading} persistor={persistor}>
			{children}
		</PersistGate>
	);
};
